import { FunctionComponent, useState, useEffect } from 'react';
import { withStyles, BodyText, Select, Flag } from 'vopay';
import { Title, Button, CircleButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { OpenDialogProps } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import useVirtualAccounts from '@/hooks/useApi/useVirtualAccounts';
import CurrencyExchangeIcon from './currencyExchange.svg';
import styles from './AddVirtualAccountDialog.module.scss';

const cn = withStyles(styles);

const AddVirtualAccountDialog: FunctionComponent<OpenDialogProps<string>> = ({ onConfirm, onCancel }) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const { virtualAccountCurrencies, fetchVirtualAccountCurrencies, pending } = useVirtualAccounts();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances.AddVirtualAccountDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  useEffect(() => {
    setSelectedCurrency('');

    const loadCurrencies = async () => {
      try {
        await fetchVirtualAccountCurrencies();
      } catch (err) {
        if (err instanceof Error) {
          alertDialog.error(tGlobal('error'), err.message);
        } else {
          alertDialog.error(
            tGlobal('error'),
            t('fetchCurrenciesError', { defaultValue: 'Failed to fetch available currencies' }),
          );
        }
      }
    };
    loadCurrencies();
  }, []);

  const handleSubmit = () => {
    if (selectedCurrency) {
      onConfirm(selectedCurrency);
    }
  };

  return (
    <div className={cn('dialogContent')}>
      <div className={cn('header')}>
        <CircleButton icon="close" colorScheme="neutral" size="large" onClick={onCancel} />
      </div>

      <div className={cn('icon')}>
        <img src={CurrencyExchangeIcon} alt="Currency Exchange" />
      </div>
      <div>
        <Title level="3" className={cn('title')}>
          {t('dialogTitle', { defaultValue: 'Add Virtual Account' })}
        </Title>

        <BodyText className={cn('description')}>
          {t('dialogDescription', { defaultValue: 'Select a currency for your new virtual account.' })}
        </BodyText>
      </div>
      <div className={cn('selectContainer')}>
        <Select
          version="2"
          name="Currency"
          placeholder={t('selectPlaceholder', { defaultValue: 'Select currency...' })}
          options={virtualAccountCurrencies.filter((c) => !c.HasVirtualAccount)}
          value={selectedCurrency}
          onChange={(e) => setSelectedCurrency(e.target.value)}
          onBlur={() => {}}
          optionValue={(o) => o.ShortForm}
          loading={pending}
          renderSelectedValue={(o, { disabled, Placeholder }) =>
            o ? (
              <div className={cn('currencyOption')}>
                <Flag className={cn(disabled && 'iconDisabled')} flag={o?.ShortForm || ''} width="22.5px" />
                <span className={cn('currencyShortForm')}>{o?.ShortForm}</span>
                <span className={cn('currencyLongForm')}>| {o?.Currency}</span>
              </div>
            ) : (
              <Placeholder />
            )
          }
          renderOption={(o, { Option }) => (
            <Option style={{ justifyContent: 'flex-start' }}>
              <div className={cn('currencyOption')}>
                <Flag flag={o?.ShortForm || ''} width="22.5px" />
                <span className={cn('currencyShortForm')}>{o?.ShortForm}</span>
                <span className={cn('currencyLongForm')}>| {o?.Currency}</span>
              </div>
            </Option>
          )}
        />
      </div>

      <div className={cn('buttonContainer')}>
        <Button colorScheme="primary" onClick={handleSubmit} disabled={!selectedCurrency}>
          {t('submitButton', { defaultValue: 'Add Virtual Account' })}
        </Button>
      </div>
    </div>
  );
};

export default AddVirtualAccountDialog;
